import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Camera } from 'lucide-react'
import React, { FC } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { useAnnotationScreenshotCapture } from './useAnnotationScreenshotCapture'

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0);
  }
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`

const TakeScreenshotButton = styled.button<{ $isCapturing: boolean }>`
  position: relative;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(59, 130, 246, 0.4);
  background: ${(props) =>
    props.$isCapturing
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.15) 100%)'
      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(96, 165, 250, 0.08) 100%)'};
  color: ${(props) =>
    props.$isCapturing ? 'rgba(96, 165, 250, 0.9)' : 'rgba(59, 130, 246, 1)'};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: ${(props) => (props.$isCapturing ? 'wait' : 'pointer')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  margin-bottom: 0.875rem;
  overflow: hidden;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled)::before {
    animation: ${shimmer} 2s infinite;
    opacity: 1;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.2) 0%,
      rgba(96, 165, 250, 0.15) 100%
    );
    border-color: rgba(59, 130, 246, 0.6);
    color: rgba(96, 165, 250, 1);
    transform: translateY(-1px);
    box-shadow:
      0 4px 12px rgba(59, 130, 246, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow:
      0 2px 6px rgba(59, 130, 246, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }

  ${(props) =>
    props.$isCapturing &&
    css`
      animation: ${pulseGlow} 2s ease-in-out infinite;
    `}

  svg {
    flex-shrink: 0;
  }
`

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(96, 165, 250, 0.3);
  border-top-color: rgba(96, 165, 250, 1);
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`

const ButtonText = styled.span`
  letter-spacing: 0.025em;
`

const ScreenshotOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 0.375rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
`

const OptionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const OptionsTitle = styled.div`
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`

const ChevronIcon = styled(ChevronDownIcon)<{ $isExpanded: boolean }>`
  width: 1rem;
  height: 1rem;
  color: rgba(255, 255, 255, 0.6);
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const OptionsContent = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${(props) =>
    props.$isExpanded ? '0 0.75rem 0.75rem 0.75rem' : '0'};
  max-height: ${(props) => (props.$isExpanded ? '500px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: ${(props) => (props.$isExpanded ? '1' : '0')};
`

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: rgba(59, 130, 246, 1);
`

const CheckboxLabel = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
`

interface AnnotationScreenshotControlsProps {
  annotationId: string
  kestrelModelId?: string
  onSuccess?: () => void
}

export const AnnotationScreenshotControls: FC<
  AnnotationScreenshotControlsProps
> = ({ annotationId, kestrelModelId, onSuccess }) => {
  const {
    isCapturing,
    showMeasurements,
    setShowMeasurements,
    showLabels,
    setShowLabels,
    showWidgets,
    setShowWidgets,
    showAnnotationMarkers,
    setShowAnnotationMarkers,
    optionsExpanded,
    setOptionsExpanded,
    handleTakeScreenshot,
  } = useAnnotationScreenshotCapture({
    annotationId,
    kestrelModelId,
    onSuccess,
  })

  return (
    <>
      <ScreenshotOptionsContainer>
        <OptionsHeader onClick={() => setOptionsExpanded(!optionsExpanded)}>
          <OptionsTitle>Screenshot Options</OptionsTitle>
          <ChevronIcon $isExpanded={optionsExpanded} />
        </OptionsHeader>
        <OptionsContent $isExpanded={optionsExpanded}>
          <CheckboxRow>
            <Checkbox
              type='checkbox'
              checked={showMeasurements}
              onChange={(e) => setShowMeasurements(e.target.checked)}
            />
            <CheckboxLabel>Show Measurements</CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <Checkbox
              type='checkbox'
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
            />
            <CheckboxLabel>Show Labels</CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <Checkbox
              type='checkbox'
              checked={showWidgets}
              onChange={(e) => setShowWidgets(e.target.checked)}
            />
            <CheckboxLabel>Show Widgets</CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <Checkbox
              type='checkbox'
              checked={showAnnotationMarkers}
              onChange={(e) => setShowAnnotationMarkers(e.target.checked)}
            />
            <CheckboxLabel>Show Annotation Markers</CheckboxLabel>
          </CheckboxRow>
        </OptionsContent>
      </ScreenshotOptionsContainer>

      <TakeScreenshotButton
        onClick={handleTakeScreenshot}
        disabled={isCapturing || !kestrelModelId}
        $isCapturing={isCapturing}
        title={
          !kestrelModelId
            ? 'No model loaded'
            : isCapturing
              ? 'Capturing screenshot...'
              : 'Capture a screenshot of the current view'
        }>
        {isCapturing ? (
          <>
            <Spinner />
            <ButtonText>Capturing...</ButtonText>
          </>
        ) : (
          <>
            <Camera size={16} />
            <ButtonText>Take Screenshot</ButtonText>
          </>
        )}
      </TakeScreenshotButton>
    </>
  )
}
